import {
  DeliveryItem,
  DeliveryOption,
  compareValue,
  type SellingPlan,
  DeliveryOptionRecurring,
} from '@/product/delivery-option';
import {Product, ProductVariant} from '@/product/types';
import parseId from '@/utilities/parse-id';
import {Plan} from '@rechargeapps/storefront-client';
import {parseGid} from '@shopify/hydrogen-react';

/**
 * Create delivery options for a product select component,
 * based on the product variants, available selling plans, and settings.
 * This allows us to dynamically populate the options in the 'Delivery' step of the product select component.
 *
 * One-time options are created for products that do not require a selling plan.
 * A recurring option is created for each subscription selling plan, populated with the variants that have the plan allocation.
 *
 * Selling plans are ignored if they:
 * - Do not have an external plan id (i.e., Recharge 'onetime' plans)
 * - Expire after a specific number of charges
 *
 * @param input - The input object
 * @returns An array of [DeliveryOption] objects
 *
 * @example Get raw data from the store(s) and create the delivery options:
 * ```ts
 *  const settings =
 *     getSettings<ProductSelectSettings>({
 *       handle: settingsHandle,
 *       type: 'product_select',
 *       followReferences: 1,
 *  });
 *  const plans = getProductSellingPlans(productId);
 *  const product = getProduct(productId);
 *
 *  const deliveryOptions = product
 *    ? createDeliveryOptions({
 *        product,
 *        sellingPlans: plans,
 *        settings,
 *      })
 *    : [];
 * ```
 */
export function createDeliveryOptions({
  product: {variants},
  sellingPlans,
  settings: {multi_pack_variants},
}: CreateDeliveryOptionsInput) {
  const options: DeliveryOption[] = [];

  options.push(createOnetimeOption(variants));

  options.push(...createBaseRecurringOptions(variants, sellingPlans));

  if (multi_pack_variants) {
    const baseVariant = smallestPackSizeVariant(variants);
    options.push(
      ...createMultipackOnetimeOptions(multi_pack_variants, baseVariant)
    );
  }

  return options.filter(({items}) => items.length > 0).sort(compareValue);
}

/**
 * Create a onetime delivery option for a product that does not require a selling plan.
 * This option is populated with the product variants.
 * The selling plan is set to null, as it is not required for onetime options.
 *
 * @param variants - The product variants to use as items
 * @returns A [DeliveryOption] object
 */
function createOnetimeOption(
  variants: ProductVariant[],
  compareAtPrice?: number
) {
  const autoExpiringPlan = variants[0].sellingPlanAllocations.find(
    ({sellingPlan}) => !sellingPlan.recurringDeliveries
  )?.sellingPlan;
  const sellingPlan = autoExpiringPlan
    ? ({
        id: parseGid(autoExpiringPlan.id).id,
        intervalUnit: 'month',
        orderIntervalFrequency: 1,
        isRecurring: false,
      } satisfies SellingPlan)
    : null;
  const items = variants.map((variant) =>
    createDeliveryItem(variant, null, compareAtPrice)
  );
  return {
    gifts: [],
    sellingPlan,
    items,
  };
}

/**
 * Create a [DeliveryItem] object from a product variant.
 * This is used to populate the items for a delivery option.
 *
 * @param variant - The product variant
 * @returns A [DeliveryItem] object
 */
function createDeliveryItem(
  variant: ProductVariant,
  plan: Plan | null,
  compareAtPrice?: number
): DeliveryItem {
  const {id, image, pack_size, price, number_of_loads, selectedOptions} =
    variant;
  const packSize = pack_size ?? 1;
  const pricePerLoad = number_of_loads ? price / number_of_loads : 0;
  const adjustedPrice = plan ? discountPrice(price, plan) : price;
  return {
    id: parseId(id),
    image,
    price: adjustedPrice,
    compareAtPrice: compareAtPrice ?? price,
    packSize,
    pricePerLoad,
    selectedOptions,
  };
}

/**
 * Calculate the discounted price for a product variant based on a selling plan.
 * The price is adjusted based on the discount type and amount of the plan.
 * If the plan does not have a discount, the original price is returned.
 *
 * @param price - The original price
 * @param plan - The selling plan
 * @returns The discounted price
 */
function discountPrice(price: number, plan: Plan) {
  const discountAmount = parseFloat(plan.discount_amount ?? '0');

  let adjustment: number;
  switch (plan.discount_type) {
    case 'fixed_amount':
      adjustment = discountAmount;
      break;
    case 'percentage':
      adjustment = price * (discountAmount / 100);
      break;
    default:
      adjustment = 0;
  }

  return price - adjustment;
}

/**
 * Create recurring delivery options for a product with subscription selling plans.
 * This is used to populate subscription options for the main product in the product select component.
 * Selling plans are ignored if they:
 * - Do not have an external plan id (i.e., Recharge 'onetime' plans)
 * - Expire after a specific number of charges
 *
 * @param variants - The product variants
 * @param sellingPlans - The selling plans
 * @returns An array of [DeliveryOption] objects
 */
function createBaseRecurringOptions(
  variants: ProductVariant[],
  sellingPlans: Plan[]
): DeliveryOptionRecurring[] {
  const options: DeliveryOptionRecurring[] = [];
  for (const plan of sellingPlans) {
    if (isRecurringPlan(plan)) {
      options.push(createRecurringOption(variants, plan));
    }
  }
  return options;
}

/**
 * Check if a selling plan is a recurring plan.
 * A recurring plan is a subscription plan that does not expire after a specific number of charges.
 *
 * @param plan - The selling plan
 * @returns True if the plan is a recurring plan
 */
export function isRecurringPlan(plan: Plan) {
  return (
    plan.external_plan_id &&
    !plan.subscription_preferences.expire_after_specific_number_of_charges
  );
}

/**
 * Create a recurring delivery option for a product with a subscription selling plan.
 * This option is populated with the variants that have the plan allocation.
 *
 * @param product - The product
 * @param plan - The selling plan
 * @returns A [DeliveryOption] object
 */
function createRecurringOption(
  variants: ProductVariant[],
  plan: Plan
): DeliveryOptionRecurring {
  const variantsFiltered = variants.filter((variant) =>
    hasPlanAllocation(variant, plan)
  );
  const items = variantsFiltered.map((variant) =>
    createDeliveryItem(variant, plan)
  );
  const sellingPlan = createSellingPlan(plan);

  return {
    gifts: [],
    items,
    sellingPlan,
  };
}

/**
 * Check if a product variant has an allocation for a selling plan.
 * If not, the plan cannot be applied to the variant.
 *
 * @param variant - The product variant
 * @param plan - The selling plan
 * @returns True if the plan can be applied to the variant
 */
function hasPlanAllocation(variant: ProductVariant, plan: Plan) {
  return variant.sellingPlanAllocations.some(
    (allocation) =>
      parseId(allocation.sellingPlan.id).toString() ===
      plan.external_plan_id?.toString()
  );
}

/**
 * Create a [SellingPlan] object from a Recharge selling plan.
 * This is used to populate the selling plan for a delivery option.
 * The selling plan is used to calculate the delivery frequency and price.
 * The plan id is used to identify the plan in the checkout.
 *
 * @param plan - The selling plan
 * @returns A [SellingPlan] object
 */
export function createSellingPlan(plan: Plan): SellingPlan {
  const planId = plan.external_plan_id
    ? plan.external_plan_id.toString()
    : null;
  const orderIntervalFrequency =
    plan.subscription_preferences.order_interval_frequency ?? 0;
  const intervalUnit = plan.subscription_preferences.interval_unit;

  return {
    id: planId,
    intervalUnit,
    orderIntervalFrequency,
    isRecurring: true,
  };
}

/**
 * Get the variant with the smallest pack size from an array of product variants.
 * If a variant has no pack_size metafield, it is treated as a single pack.
 * This allows us to determine which variant in the array is the base variant for multi-pack products.
 * @param variants - The product variants
 * @returns The variant with the smallest pack size
 */
function smallestPackSizeVariant(variants: ProductVariant[]) {
  return variants.reduce(
    (smallest, variant) => {
      return (variant.pack_size ?? 1) < (smallest.pack_size ?? 1)
        ? variant
        : smallest;
    },
    {pack_size: Infinity} as ProductVariant
  );
}

/**
 * Create one-time delivery options for a product with multi-pack variants.
 * This allows us to show customers the price of purchasing a multi-pack instead of the base onetime product.
 * The options are populated with the variants that have the same pack size.
 * The compare at price is calculated based on the base variant.
 *
 * @param multiPackVariants - The multi-pack variants
 * @param baseVariant - The base variant
 * @returns An array of [DeliveryOption] objects
 */
function createMultipackOnetimeOptions(
  multiPackVariants: ProductVariant[],
  baseVariant: ProductVariant
) {
  const packSizes = new Set(multiPackVariants.map((v) => v.pack_size));

  return Array.from(packSizes.values()).map(createMultipackOption);

  /**
   * Create a one-time delivery option for a specific pack size.
   */
  function createMultipackOption(packSize?: number | null) {
    const variants = multiPackVariants.filter(
      ({pack_size}) => pack_size === packSize
    );
    const compareAtPrice = calculateCompareAtPrice(variants[0], baseVariant);
    return createOnetimeOption(variants, compareAtPrice);
  }
}

/**
 * Calculate the compare at price for a multi-pack variant, based on the base variant.
 * This allows us to show customers how much they are saving by purchasing a multi-pack
 * instead of the base onetime product.
 * @param variant - The multi-pack variant
 * @param baseVariant - The base variant
 * @returns The compare at price
 */
function calculateCompareAtPrice(
  variant: ProductVariant,
  baseVariant: ProductVariant
): number {
  const unitPrice = baseVariant.pack_size
    ? baseVariant.price / baseVariant.pack_size
    : baseVariant.price;
  return variant.pack_size ? unitPrice * variant.pack_size : baseVariant.price;
}

/**
 * The input object for the createDeliveryOptions function.
 */
export type CreateDeliveryOptionsInput = {
  product: Product;
  sellingPlans: Plan[];
  settings: CreateDeliveryOptionsSettings;
};

// TODO: implement support for yearly subscription variants
// These values may be accessed via the settings object.
export type CreateDeliveryOptionsSettings = {
  multi_pack_variants?: ProductVariant[];
};
